import { css } from 'design'
import { motion } from 'framer-motion'
import { Badge } from 'primitives/badge'
import React from 'react'

import { WidgetLogo } from '../../images'
import { getCurrentMember } from '../../reduxes/slices/current/selectors'
import { useSelectorDelegateStore } from '../../reduxes/stores/root_store/hooks'
import { LAUNCHER_HEIGHT, WIDGET_LAYOUT } from './constants'

type Props = {
  rootWelcomeMessage: any
  noOfPostersLoaded: number
  isOpen: boolean
  toggleLayout: any
}

export const WidgetLauncher = ({ rootWelcomeMessage, noOfPostersLoaded, isOpen, toggleLayout }: Props) => {
  const currentMember = useSelectorDelegateStore(getCurrentMember)

  return (
    <div
      className={launchButtonContainerStyles({
        hide: !rootWelcomeMessage.preview_video_poster
          ? false
          : noOfPostersLoaded < 2,
      })}
    >
      {rootWelcomeMessage && !isOpen ? (
        <motion.div
          key={'video-launcher'}
          initial={{ opacity: 0 }}
          animate={type !== 'widget' ? {} : { opacity: 1 }}
        >
          <video
            src={rootWelcomeMessage.preview_video}
            poster={rootWelcomeMessage.preview_video_poster}
            onClick={() => toggleLayout(WIDGET_LAYOUT.WIDGET_MINIMISED)}
            autoPlay={true}
            muted
            playsInline
            disablePictureInPicture
            className={welcomeVideoStyles()}
          />
          <div className={BGStyles()} />
          <div className={insetBoxShadowStyles()} />
          <div className={speechBubbleTick({ top: true })} />
          <div className={speechBubbleTick({ top: false })} />
        </motion.div>
      ) : (
        <motion.div
          key={'logo-launcher'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <WidgetLogo
            role={'button'}
            width={LAUNCHER_INNER_SIZE}
            height={LAUNCHER_INNER_SIZE}
            onClick={() => toggleLayout(WIDGET_LAYOUT.WIDGET_MINIMISED)}
            className={buttonStyles()}
          />
        </motion.div>
      )}
      {!!currentMember?.badge && (
        <Badge
          css={{
            position: 'absolute',
            top: 4,
            right: 6,
            transform: 'translate(10px, -8px)',
            zIndex: 2,
          }}
        >
          {currentMember?.badge}
        </Badge>
      )}
    </div>
  )
}

// eslint-disable-next-line
// @ts-ignore
const type = window.speakeasySettings?.type

const LAUNCHER_INNER_SIZE = LAUNCHER_HEIGHT - 50

const buttonStyles = css({
  cursor: 'pointer',
})

const launchButtonContainerStyles = css({
  position: 'relative',
  width: LAUNCHER_INNER_SIZE,
  height: LAUNCHER_INNER_SIZE,
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  margin: '20px 30px 30px 30px',

  '>:first-child': {
    position: 'relative',
    width: '100%',
    height: '100%',

    '>*': {
      boxSizing: 'border-box',
    },
  },

  variants: {
    hide: {
      true: {
        display: 'none',
      },
    },
  },
})

const X = LAUNCHER_INNER_SIZE * 0.09

const welcomeVideoStyles = css({
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  borderRadius: '50%',
  border: '4px solid $grey50',
  boxShadow: 'rgb(0,0,0,0.35) 0px 1px 6px',
})

const insetBoxShadowStyles = css({
  position: 'absolute',
  width: LAUNCHER_INNER_SIZE,
  height: LAUNCHER_INNER_SIZE,
  inset: 0,
  margin: '0 auto',
  boxShadow: 'rgb(0,0,0,0.35) 0px 0px 20px inset',
  borderRadius: '50%',
  border: '4px solid $grey50',
  pointerEvents: 'none',
})

const speechBubbleTick = css({
  position: 'absolute',
  bottom: '1px',
  right: '10px',
  backgroundColor: '$grey50',
  textAlign: 'left',
  width: `${X}px`,
  height: `${X}px`,
  borderTopRightRadius: '30%',
  transform: 'rotate(88deg) skewX(-30deg) scale(1,.866)',

  '&:before, &:after': {
    content: '',
    position: 'absolute',
    backgroundColor: 'inherit',
    width: `${X}px`,
    height: `${X}px`,
    borderTopRightRadius: '30%',
  },

  '&:before': {
    transform:
      'rotate(-135deg) skewX(-45deg) scale(1.414,.707) translate(0,-50%)',
  },

  '&:after': {
    transform: 'rotate(135deg) skewY(-45deg) scale(.707,1.414) translate(50%)',
  },

  variants: {
    top: {
      false: {
        zIndex: -1,
        boxShadow: `rgb(0 0 0 / 30%) -${X / 4}px ${X / 4}px ${X / 1.5}px ${
          X / 2
        }px`,
      },
    },
  },
})

const BGStyles = css({
  background: '$grey50',
  borderRadius: '50%',
  position: 'absolute',
  inset: 0,
  zIndex: -1,
})
